import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/apiClient';
import { useQueryConfig } from './useQueryConfig';
import config from '../utils/config';

/**
 * Custom hook for Analytics API operations
 */
export const useAnalytics = () => {
  const queryClient = useQueryClient();
  const { queryKeys } = useQueryConfig();

  // Query to fetch basic stats
  const useBasicStats = (projectId, year, month, useOptimized = true) => {
    console.log('useBasicStats called with:', { projectId, year, month, useOptimized });
    return useQuery({
      queryKey: ['analytics', 'stats', projectId, year, month, useOptimized],
      queryFn: async () => {
        // Use the optimized endpoint for large datasets
        const endpoint = useOptimized 
          ? `/analytics/projects/${projectId}/stats/aggregated` 
          : `/analytics/projects/${projectId}/stats`;
          
        const url = `${config.API_BASE_URL}${endpoint}`;
        console.log('Fetching basic stats from:', url);
        
        try {
          const response = await apiClient.get(endpoint, {
            params: { year, month }
          });
          return response;
        } catch (error) {
          // If the optimized endpoint fails, fall back to the regular endpoint
          if (useOptimized && error.response && error.response.status >= 400) {
            console.log('Optimized endpoint failed, falling back to regular endpoint');
            const fallbackResponse = await apiClient.get(`/analytics/projects/${projectId}/stats`, {
              params: { year, month }
            });
            return fallbackResponse;
          }
          throw error;
        }
      },
      enabled: !!projectId && !!year && !!month,
      retry: (failureCount, error) => {
        // Don't retry if we're already using the non-optimized endpoint
        if (!useOptimized) return failureCount < 3;
        
        // If it's a MongoDB memory error, don't retry with the same endpoint
        if (error?.response?.data?.message?.includes('Sort exceeded memory limit')) {
          return false;
        }
        
        return failureCount < 3;
      },
    });
  };

  // Query to fetch existing report
  const useReport = (projectId, year, month) => {
    console.log('useReport called with:', { projectId, year, month });
    return useQuery({
      queryKey: ['analytics', 'report', projectId, year, month],
      queryFn: async () => {
        const url = `${config.API_BASE_URL}/analytics/projects/${projectId}/report`;
        console.log('Fetching report from:', url);
        const response = await apiClient.get(`/analytics/projects/${projectId}/report`, {
          params: { year, month }
        });
        return response;
      },
      enabled: !!projectId && !!year && !!month,
    });
  };

  // Mutation to generate new report
  const useGenerateReport = () => {
    return useMutation({
      mutationFn: async ({ projectId, year, month }) => {
        console.log('Generating report for:', { projectId, year, month });
        const response = await apiClient.post(`/analytics/projects/${projectId}/report`, {
          year,
          month
        });
        return response;
      },
      onSuccess: (_, variables) => {
        // Invalidate the report query to refetch with new data
        queryClient.invalidateQueries({
          queryKey: ['analytics', 'report', variables.projectId, variables.year, variables.month],
        });
      },
    });
  };

  return {
    useBasicStats,
    useReport,
    useGenerateReport,
  };
};

export default useAnalytics; 